import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FaImage, FaSpinner, FaTimes } from 'react-icons/fa';
import { postsAPI } from '../services/api';
import AppBar from '../components/AppBar';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
`;

const CreatePostContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background-color: #111;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #333;
  margin-top: 20px;

  @media (max-width: 768px) {
    padding: 24px 16px;
    margin-top: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #333;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 28px;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: #fff;
    background-color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 16px;
  background-color: #222;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &::placeholder {
    color: #666;
  }
`;

const ImageUploadSection = styled.div`
  border: 2px dashed #333;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  transition: border-color 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #ff6b35;
  }
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadIcon = styled.div`
  font-size: 48px;
  color: #666;
  margin-bottom: 16px;
`;

const ImageUploadText = styled.div`
  color: #ccc;
  font-size: 16px;
  margin-bottom: 8px;
`;

const ImageUploadSubtext = styled.div`
  color: #666;
  font-size: 14px;
`;

const ImagePreview = styled.div`
  position: relative;
  margin-top: 16px;
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 8px;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 107, 53, 0.8);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: #ccc;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;

  &:hover {
    background-color: #333;
    color: #fff;
  }
`;

const CreateButton = styled.button`
  background-color: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: #45a049;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CreatePostScreen = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleImageUploadClick = () => {
    document.getElementById('image-upload').click();
  };

  const onSubmit = async (data) => {
    if (!data.content.trim() && !selectedImage) {
      toast.error('Please add some content or an image');
      return;
    }

    setIsLoading(true);
    try {
      const postData = {
        content: data.content.trim(),
        // In a real app, you would upload the image to a server first
        // and then use the returned URL
        image: selectedImage ? imagePreview : null,
      };

      await postsAPI.createPost(postData);
      toast.success('Post created successfully!');
      navigate('/feed');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/feed');
  };

  return (
    <Container>
      <AppBar />
      <CreatePostContainer>
        <Header>
          <Title>Create Post</Title>
          <CloseButton onClick={handleCancel}>
            <FaTimes />
          </CloseButton>
        </Header>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="content">What's on your mind?</Label>
            <Textarea
              id="content"
              placeholder="Share your thoughts..."
              {...register('content', {
                maxLength: {
                  value: 1000,
                  message: 'Post content must be less than 1000 characters',
                },
              })}
            />
            {errors.content && (
              <div style={{ color: '#ff6b35', fontSize: '12px', marginTop: '4px' }}>
                {errors.content.message}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Add Image (Optional)</Label>
            <ImageUploadSection onClick={handleImageUploadClick}>
              <ImageUploadInput
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
              />
              <ImageUploadIcon>
                <FaImage />
              </ImageUploadIcon>
              <ImageUploadText>Click to upload an image</ImageUploadText>
              <ImageUploadSubtext>JPG, PNG, GIF up to 5MB</ImageUploadSubtext>
            </ImageUploadSection>

            {imagePreview && (
              <ImagePreview>
                <PreviewImage src={imagePreview} alt="Preview" />
                <RemoveImageButton onClick={handleRemoveImage}>
                  <FaTimes />
                </RemoveImageButton>
              </ImagePreview>
            )}
          </FormGroup>

          <ButtonGroup>
            <CancelButton type="button" onClick={handleCancel}>
              Cancel
            </CancelButton>
            <CreateButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <FaSpinner className="fa-spin" />
                  Creating...
                </>
              ) : (
                'Create Post'
              )}
            </CreateButton>
          </ButtonGroup>
        </Form>
      </CreatePostContainer>
    </Container>
  );
};

export default CreatePostScreen; 